import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BookOpenCheck, Search, ChevronRight, Lock, Loader2, FileDown, UserPlus, CheckCircle2 } from 'lucide-react';

const POEPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [qualifications, setQualifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [yearFilter, setYearFilter] = useState('all');
    const [qualFilter, setQualFilter] = useState('all');
    const [assignedFilter, setAssignedFilter] = useState('all');
    const [selected, setSelected] = useState([]);
    const [assigning, setAssigning] = useState(false);

    useEffect(() => { fetchStudents(); fetchQualifications(); }, []);

    const fetchStudents = async () => {
        try {
            const res = await axios.get(`${API}/poe/students`, { headers: getAuthHeaders() });
            setStudents(res.data);
        } catch (e) {
            toast.error('Failed to load POE students');
        } finally {
            setLoading(false);
        }
    };

    const fetchQualifications = async () => {
        try {
            const res = await axios.get(`${API}/poe/qualifications`, { headers: getAuthHeaders() });
            setQualifications(res.data.qualifications || []);
        } catch (e) { /* silent */ }
    };

    const qualTitle = (code) => qualifications.find(q => q.code === code)?.title || code;

    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const sa = (a.name || '').split(' ').pop().toLowerCase();
        const sb = (b.name || '').split(' ').pop().toLowerCase();
        return sa.localeCompare(sb);
    });

    const filtered = sortBySurname(students.filter(s => {
        if (yearFilter !== 'all' && s.year !== parseInt(yearFilter)) return false;
        if (qualFilter !== 'all' && !(s.qualifications || []).includes(qualFilter)) return false;
        if (assignedFilter === 'assigned' && !s.assigned) return false;
        if (assignedFilter === 'unassigned' && s.assigned) return false;
        const q = search.toLowerCase();
        return s.name.toLowerCase().includes(q) || s.student_id.toLowerCase().includes(q);
    }));

    const toggleSelect = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const assign = async (ids) => {
        if (!ids.length) { toast.error('No students selected'); return; }
        setAssigning(true);
        try {
            const res = await axios.post(`${API}/poe/assign`, { student_ids: ids }, { headers: getAuthHeaders() });
            toast.success(res.data.message);
            setSelected([]);
            fetchStudents();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to assign');
        } finally {
            setAssigning(false);
        }
    };

    const exportPdf = async (s) => {
        try {
            const res = await axios.get(`${API}/poe/student/${s.id}/pdf`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `POE_${s.student_id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('POE exported');
        } catch (e) {
            toast.error('Failed to export POE');
        }
    };

    const unassignedSelectable = filtered.filter(s => !s.assigned).map(s => s.id);

    const pillClass = (active) =>
        `px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active
            ? 'bg-ica-navy text-white border-ica-navy shadow-sm'
            : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700 hover:border-ica-navy dark:hover:border-ica-gold'}`;

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="poe-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Portfolio of Evidence</h1>
                        <p className="text-gray-500 dark:text-slate-400 mt-1">Assign, mark and export the Practical Module Assessment Log per student</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={() => assign(selected)}
                            disabled={assigning || selected.length === 0}
                            
                            data-testid="poe-assign-selected-btn"
                        >
                            {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                            Assign Selected ({selected.length})
                        </Button>
                    </div>
                </div>

                {/* Pills + filters */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 flex-wrap" data-testid="poe-year-pills">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 mr-1">Year</span>
                        {['all', '1', '2', '3'].map(y => (
                            <button key={y} onClick={() => setYearFilter(y)} data-testid={`poe-year-pill-${y}`} className={pillClass(yearFilter === y)}>
                                {y === 'all' ? 'All Years' : `Year ${y}`}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap" data-testid="poe-qual-pills">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 mr-1">Qualification</span>
                        <button onClick={() => setQualFilter('all')} data-testid="poe-qual-pill-all" className={pillClass(qualFilter === 'all')}>All</button>
                        {qualifications.map(q => (
                            <button key={q.code} onClick={() => setQualFilter(q.code)} title={q.title}
                                data-testid={`poe-qual-pill-${q.code.replace(/\//g, '-')}`} className={pillClass(qualFilter === q.code)}>
                                {q.code}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative max-w-md flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input data-testid="poe-search" placeholder="Search by name or ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                        </div>
                        <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="poe-assigned-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Students</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="unassigned">Not Assigned</SelectItem>
                            </SelectContent>
                        </Select>
                        {unassignedSelectable.length > 0 && (
                            <Button variant="outline" onClick={() => assign(unassignedSelectable)} disabled={assigning} data-testid="poe-assign-all-btn">
                                Assign All Filtered ({unassignedSelectable.length})
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-400">Loading...</div>
                    ) : filtered.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full ica-table">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-4 w-10"></th>
                                        <th className="px-6 py-4 text-left">Student</th>
                                        <th className="px-6 py-4 text-left">ID</th>
                                        <th className="px-6 py-4 text-center">Year</th>
                                        <th className="px-6 py-4 text-left">Qualification</th>
                                        <th className="px-6 py-4 text-center">POE Status</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((s) => (
                                        <tr key={s.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50/50 dark:hover:bg-slate-700/30" data-testid={`poe-student-row-${s.id}`}>
                                            <td className="px-4 py-4 text-center">
                                                {!s.assigned && (
                                                    <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} data-testid={`poe-select-${s.id}`} className="w-4 h-4 accent-ica-navy" />
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-ica-navy/10 rounded-full flex items-center justify-center">
                                                        <BookOpenCheck className="w-4 h-4 text-ica-navy dark:text-ica-light-gold" />
                                                    </div>
                                                    <span className="font-medium text-gray-900 dark:text-slate-100">{s.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 dark:text-slate-400 font-mono text-sm">{s.student_id}</td>
                                            <td className="px-6 py-4 text-center text-sm text-gray-600 dark:text-slate-400">Year {s.year}</td>
                                            <td className="px-6 py-4" data-testid={`poe-student-quals-${s.id}`}>
                                                <div className="flex flex-wrap gap-1">
                                                    {(s.qualifications || []).length > 0 ? (s.qualifications || []).map(c => (
                                                        <span key={c} title={qualTitle(c)} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-ica-navy/10 text-ica-navy dark:bg-ica-gold/15 dark:text-ica-light-gold">{c}</span>
                                                    )) : <span className="text-xs text-gray-400">—</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {s.assigned ? (
                                                    <div className="flex items-center justify-center gap-2">
                                                        <StatusPill tone="success" icon={CheckCircle2}>Assigned · {s.progress} marked</StatusPill>
                                                        {s.locked && <Lock className="w-3.5 h-3.5 text-amber-500" />}
                                                    </div>
                                                ) : (
                                                    <StatusPill tone="empty">Not assigned</StatusPill>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    {s.assigned ? (
                                                        <>
                                                            <Button variant="ghost" size="sm" onClick={() => exportPdf(s)} className="text-slate-500 hover:text-ica-navy dark:text-slate-400 h-8 w-8 p-0" title="Export PDF" data-testid={`poe-export-${s.id}`}>
                                                                <FileDown className="w-4 h-4" />
                                                            </Button>
                                                            <Button size="sm" onClick={() => navigate(`/poe/student/${s.id}`)} data-testid={`poe-open-${s.id}`}>
                                                                Open <ChevronRight className="w-4 h-4 ml-1" />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button variant="outline" size="sm" onClick={() => assign([s.id])} disabled={assigning} data-testid={`poe-assign-${s.id}`}>
                                                            <UserPlus className="w-4 h-4 mr-1" /> Assign
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <BookOpenCheck className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No students found</h3>
                            <p className="text-gray-500 dark:text-slate-400">Add students or adjust your filters</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default POEPage;
